import { useEffect, useId, useRef, useState } from "react";
import { Volume2, VolumeX, Maximize2 } from "lucide-react";

// Autoplay/muted/looping video with a subtle unmute toggle.
// Only one SoundVideo across the page plays audio at a time (coordinated via a window event).
export const SoundVideo = ({ src, poster, className = "", testid, onExpand }) => {
  const ref = useRef(null);
  const id = useId();
  const [soundOn, setSoundOn] = useState(false);

  const toggle = (e) => {
    e?.stopPropagation();
    const v = ref.current;
    if (!v) return;
    const next = !soundOn;
    if (next) {
      window.dispatchEvent(new CustomEvent("soundvideo:unmute", { detail: id }));
      v.muted = false;
      v.volume = 1;
      v.play().catch(() => {});
    } else {
      v.muted = true;
    }
    setSoundOn(next);
  };

  useEffect(() => {
    const onOther = (e) => {
      if (e.detail !== id && ref.current) {
        ref.current.muted = true;
        setSoundOn(false);
      }
    };
    window.addEventListener("soundvideo:unmute", onOther);
    return () => window.removeEventListener("soundvideo:unmute", onOther);
  }, [id]);

  return (
    <div
      className={`group relative h-full w-full ${onExpand ? "cursor-zoom-in" : ""}`}
      data-testid={testid}
      onClick={onExpand}
      role={onExpand ? "button" : undefined}
      tabIndex={onExpand ? 0 : undefined}
      onKeyDown={onExpand ? (e) => (e.key === "Enter" || e.key === " ") && onExpand() : undefined}
    >
      <video
        ref={ref}
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className={className}
      />
      {onExpand && (
        <span
          aria-hidden
          className="pointer-events-none absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-navy/60 text-white opacity-0 backdrop-blur-md transition-opacity duration-200 group-hover:opacity-100"
        >
          <Maximize2 className="h-4 w-4" />
        </span>
      )}
      <button
        type="button"
        onClick={toggle}
        aria-label={soundOn ? "Mute video" : "Unmute video"}
        data-testid={testid ? `${testid}-sound-btn` : "sound-btn"}
        className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-navy/60 text-white backdrop-blur-md transition-all duration-200 hover:scale-105 hover:border-revo-green hover:text-revo-green"
      >
        {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
      </button>
    </div>
  );
};
